import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import BlogAds from '../sidebar/BlogAds';
import BlogCategory from '../sidebar/BlogCategory';
import BlogLatestPosts from '../sidebar/BlogLatestPosts';
import BlogSearch from '../sidebar/BlogSearch';
import BlogTags from '../sidebar/BlogTags';
import { getBlogPostBySlug } from '../../data/content';

const resolveAssetUrl = (src) => {
  if (!src) return '';
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}${src.startsWith('/') ? src : `/${src}`}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const renderInlineBold = (text) => {
  const parts = [];
  let remaining = String(text ?? '');
  let key = 0;

  while (remaining.length) {
    const start = remaining.indexOf('**');
    if (start === -1) {
      parts.push(remaining);
      break;
    }
    const end = remaining.indexOf('**', start + 2);
    if (end === -1) {
      parts.push(remaining);
      break;
    }
    if (start > 0) parts.push(remaining.slice(0, start));
    parts.push(<strong key={`b-${key++}`}>{remaining.slice(start + 2, end)}</strong>);
    remaining = remaining.slice(end + 2);
  }

  return parts;
};

const parseMarkdown = (markdown) => {
  const lines = String(markdown ?? '').split('\n');
  const blocks = [];
  let i = 0;

  const consumeParagraph = () => {
    const buffer = [];
    while (i < lines.length && lines[i].trim() !== '') {
      buffer.push(lines[i].trim());
      i += 1;
    }
    const text = buffer.join(' ');
    if (text) blocks.push({ type: 'p', text });
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    if (trimmed === '---') {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const text = headingMatch[2];
      blocks.push({ type: 'h', level, text });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    consumeParagraph();
  }

  return blocks;
};

const BlogContentDetails = () => {
  const { slug } = useParams();
  const post = useMemo(() => getBlogPostBySlug(slug), [slug]);
  const blocks = useMemo(() => parseMarkdown(post?.content), [post?.content]);
  const hero = resolveAssetUrl(post?.images?.hero || post?.images?.thumbnail);

  if (!post) {
    return (
      <section className="blog-single">
        <div className="container">
          <div className="row">
            <div className="col-xl-8 col-lg-7">
              <div className="blog-single__left">
                <h2>Post not found</h2>
                <p>That event or guide may have been moved or removed.</p>
                <Link to="/events" className="thm-btn">Back to Events</Link>
              </div>
            </div>
            <div className="col-xl-4 col-lg-5">
              <div className="sidebar">
                <BlogSearch />
                <BlogCategory />
                <BlogLatestPosts />
                <BlogTags />
                <BlogAds />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="blog-single">
      <div className="container">
        <div className="row">
          <div className="col-xl-8 col-lg-7">
            <div className="blog-single__left">
              <div className="blog-one__single">
                <div className="blog-one__single-img">
                  <img
                    src={hero || resolveAssetUrl('/assets/images/blog/blog-v1-img1.jpg')}
                    alt={post.title}
                    onError={(e) => {
                      e.currentTarget.src = resolveAssetUrl('/assets/images/blog/blog-v1-img1.jpg');
                    }}
                  />
                </div>

                <div className="blog-one__single-content">
                  <div className="blog-one__single-content-meta-box">
                    <ul className="list-unstyled">
                      <li>
                        <div className="inner">
                          <div className="icon">
                            <span className="icon-user"></span>
                          </div>
                          <div className="text">
                            <Link to="/events">{post.author || 'Fresh SWFL'}</Link>
                          </div>
                        </div>
                      </li>

                      <li>
                        <div className="inner">
                          <div className="icon">
                            <span className="icon-calendar"></span>
                          </div>
                          <div className="text">
                            <Link to="/events">{formatDate(post.publishDate || post.updatedDate)}</Link>
                          </div>
                        </div>
                      </li>
                    </ul>
                    <div className="btn-box">
                      <Link to="/events"><span className="fas fa-share"></span></Link>
                    </div>
                  </div>

                  <p className="blog-one__single-content-tagline">{post.category || 'Updates'}</p>
                  <h2>{post.title}</h2>
                  <div className="line"></div>

                  <div className="text">
                    {blocks.map((block, idx) => {
                      if (block.type === 'hr') return <hr key={`hr-${idx}`} />;
                      if (block.type === 'h') {
                        const Tag = `h${Math.min(6, Math.max(2, block.level))}`;
                        return <Tag key={`h-${idx}`}>{renderInlineBold(block.text)}</Tag>;
                      }
                      if (block.type === 'ul') {
                        return (
                          <ul key={`ul-${idx}`}>
                            {block.items.map((item, j) => (
                              <li key={`ul-${idx}-${j}`}>{renderInlineBold(item)}</li>
                            ))}
                          </ul>
                        );
                      }
                      if (block.type === 'ol') {
                        return (
                          <ol key={`ol-${idx}`}>
                            {block.items.map((item, j) => (
                              <li key={`ol-${idx}-${j}`}>{renderInlineBold(item)}</li>
                            ))}
                          </ol>
                        );
                      }
                      return <p key={`p-${idx}`}>{renderInlineBold(block.text)}</p>;
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-lg-5">
            <div className="sidebar">
              <BlogSearch />
              <BlogCategory />
              <BlogLatestPosts />
              <BlogTags />
              <BlogAds />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogContentDetails;
